import React, { useState, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Tile from './Tile'
import TextButton from 'components/Luxkit/Button/TextButton'
import config from 'constants/config'
import Heading from 'components/Luxkit/Typography/Heading'
import FakeInput from 'components/Common/Form/Input/FakeInput'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import ListItem from 'components/Luxkit/List/ListItem'
import useToggle from 'hooks/useToggle'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'

const TilesWrapper = styled.div`
  display: grid;
  gap: ${rem(4)};
  justify-content: center;
  margin-top: ${rem(24)};
  grid-template-columns: repeat(auto-fit, calc(50% - ${rem(4)}));
  align-items: center;

  @media (min-width: ${() => config.bp.phablet}px) {
    gap: ${rem(12)};
    grid-template-columns: repeat(auto-fit, calc(33.3% - ${rem(12)}));
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(auto-fit, calc(25% - ${rem(12)}));
  }
`

const FilterWrapper = styled.div`
  max-width: ${rem(275)};
  margin: auto;
`

const StyledGrid = styled(LayoutContainer)`
  margin-top: ${rem(16)};
`

const ButtonWrapper = styled(Group)`
  margin-top: ${rem(24)};
`

function getUniqueListBy(arr, key) {
  return [...new Map(arr.map(item => [item[key], item])).values()]
}

const defaultOption = { label: 'Show All', value: 'Show All' }
interface Props {
  title: string;
  tiles: Array<App.HubTile>;
  maxTilesVisible: number;
}

function TileNavigation(props: Props) {
  const { tiles } = props
  const [maxNumberOfTiles, setMaxNumberOfTiles] = useState(props.maxTilesVisible)
  const [selectedOption, setOptions] = useState(defaultOption)
  const triggerRef = useRef(null)
  const [open, , openDropdown, closeDropdown] = useToggle()

  const options = useMemo(() => ([
    defaultOption,
    ...getUniqueListBy(tiles, 'filterLabel').map((n: App.HubTile) => ({
      label: n.filterLabel,
      value: n.filterLabel,
    }))]),
  [tiles])

  const onFilterChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    const option = options.find(option => option.value === e.currentTarget.dataset.value)
    setOptions(option!)
    closeDropdown()
  }

  const filteredTiles = useMemo(() => {
    if (selectedOption.value === 'Show All') {
      return tiles
    }

    return tiles.filter(tile => tile.filterLabel === selectedOption.value)
  }, [selectedOption.value, tiles])

  return <>
    <Heading variant="heading3" as="h2" align="center">{props.title}</Heading>
    <StyledGrid>
      <FilterWrapper>
        <FakeInput
          value={selectedOption.label}
          ref={triggerRef}
          onClick={openDropdown}
          endIcon={<DropdownChevron open={open}/>}
        />
        <DropdownList
          size="S"
          triggerRef={triggerRef}
          anchorRef={triggerRef}
          open={open}
          onClose={closeDropdown}
          title="Travel guides"
        >
          {options.map(option => <ListItem
            key={option.value}
            data-value={option.value}
            onClick={onFilterChange}
            title={option.label}
            selected={option.value === selectedOption.value }
          />)}
        </DropdownList>
      </FilterWrapper>
      <TilesWrapper>
        {filteredTiles.slice(0, maxNumberOfTiles).map((tile: App.HubTile, i: number) => (
          <Tile
            linkTo={tile.tileLink}
            href={tile.hrefLink}
            key={tile.title + i}
            image={tile.image}
            title={tile.title}
            shortText={tile.shortText}
            linkText={tile.linkText}
            mobileAspectRatio="4:3"
            mobileWidth="200px"
            tabletAspectRatio="5:4"
            tabletWidth="300px"
          />
        ))}
      </TilesWrapper>
      {tiles.length > maxNumberOfTiles &&
        <ButtonWrapper direction="horizontal" horizontalAlign="center">
          <TextButton
            kind="secondary"
            onClick={() => setMaxNumberOfTiles(maxNumberOfTiles + 8)}>
            View more
          </TextButton>
        </ButtonWrapper>}
    </StyledGrid>
  </>
}

export default TileNavigation
